/**
 * D3 Validación — Scoring de verificaciones cruzadas y espaciales
 *
 * Evalúa la fiabilidad del resultado mediante:
 *   - Validación espacial (coordenada dentro del municipio declarado)
 *   - Validación cruzada multi-fuente (≥2 fuentes confirman)
 *   - Penalización por discrepancia entre fuentes
 *
 * @module services/scoring/d3Validacion
 * @version 1.0.0
 */

import type { D3ValidacionInput, D3ValidacionResult } from '../../types/scoring';
import type { InfrastructureType } from '../../lib/InfrastructureClassifier';
import { validateSupramunicipal } from '../validation/MunicipalBoundaryValidator';

// ============================================================================
// SUPRAMUNICIPAL — Contexto para infraestructuras fuera del BBox
// ============================================================================

/**
 * Contexto opcional para validación supramunicipal.
 * Permite que D3 no penalice infraestructuras legítimamente supramunicipales
 * (bomberos comarcales, hospitales provinciales, EDAR mancomunadas, etc.).
 *
 * @see validateSupramunicipal() en MunicipalBoundaryValidator.ts
 */
export interface SupramunicipalContext {
  /** Coordenada X en UTM30 (EPSG:25830) */
  x: number;
  /** Coordenada Y en UTM30 (EPSG:25830) */
  y: number;
  /** Código INE del municipio del PTEL (5 dígitos) */
  codigoINE: string;
  /** Tipología clasificada de la infraestructura */
  infrastructureType: InfrastructureType;
}

// ============================================================================
// CONFIGURACIÓN
// ============================================================================

/**
 * Score base de D3: punto de partida neutral.
 * Sin ninguna validación, el resultado tiene confianza moderada.
 */
const BASE_SCORE = 50;

/**
 * Bonus cuando la coordenada cae dentro del municipio declarado.
 * Valida coherencia espacial básica.
 */
const SPATIAL_VALID_BONUS = 25;

/**
 * Penalización cuando la coordenada cae FUERA del municipio declarado.
 * Indica posible error de geocodificación o datos erróneos.
 */
const SPATIAL_INVALID_PENALTY = 30;

/**
 * Bonus base por validación cruzada exitosa (≥2 fuentes confirman).
 * Se aplica incluso sin crossValidationScore definido.
 */
const CROSS_VALIDATION_BASE_BONUS = 10;

/**
 * Penalización por ausencia total de fuentes confirmantes.
 * Sin ninguna fuente, el resultado es menos fiable.
 */
const NO_SOURCES_PENALTY = 5;

/**
 * Umbrales de discrepancia (metros) y penalización asociada.
 * Ordenados de menor a mayor distancia.
 */
const DISCREPANCY_THRESHOLDS: ReadonlyArray<{ maxMeters: number; penalty: number }> = [
  { maxMeters: 25,  penalty: 0 },   // ±25 m: coherencia excelente
  { maxMeters: 100, penalty: 10 },   // ±100 m: discrepancia menor
  { maxMeters: 500, penalty: 20 },   // ±500 m: discrepancia moderada
  { maxMeters: Infinity, penalty: 30 }, // >500 m: discrepancia severa
];

/**
 * Rangos de bonus según crossValidationScore.
 * Solo se aplican cuando sourcesConfirming ≥ 2.
 */
const CROSS_SCORE_BONUS: ReadonlyArray<{ minScore: number; bonus: number }> = [
  { minScore: 80, bonus: 20 },  // Excelente coherencia multi-fuente
  { minScore: 60, bonus: 15 },  // Buena coherencia
  { minScore: 40, bonus: 10 },  // Coherencia aceptable
  { minScore: 0,  bonus: 5 },   // Coherencia mínima
];

// ============================================================================
// FUNCIÓN PRINCIPAL
// ============================================================================

/**
 * Calcula el componente D3 Validación del scoring v2.
 *
 * Algoritmo:
 * 1. Score base neutral (50)
 * 2. ± Validación espacial (dentro/fuera del municipio)
 * 3. + Bonus por validación cruzada (≥2 fuentes confirman)
 *    − Penalización si 0 fuentes
 * 4. − Penalización por discrepancia entre fuentes
 * 5. Clamp [0, 100]
 *
 * @example
 * ```ts
 * const d3 = computeD3Validacion({
 *   spatialValid: true,
 *   crossValidationScore: 85,
 *   sourcesConfirming: 3,
 *   maxDiscrepancyMeters: 12,
 * });
 * // d3.score → 95 (50 base + 25 spatial + 20 cross, disc ±12m ok)
 * ```
 */
export function computeD3Validacion(
  input: D3ValidacionInput,
  supramunicipalCtx?: SupramunicipalContext,
): D3ValidacionResult {
  const { spatialValid, crossValidationScore, sourcesConfirming, maxDiscrepancyMeters, primaryConfidence } = input;

  // 1. Score base
  let rawScore = BASE_SCORE;

  // 2. Validación espacial
  let spatialBonus = computeSpatialBonus(spatialValid);

  // 2b. Override supramunicipal: si fuera de BBox pero infra legítimamente supramunicipal
  //     En vez de penalizar -30, usar score degradado [0, +20] según distancia/tipología.
  //     Fórmula: bonus = round(((supraScore - 70) / 25) * 20)
  //       - supraScore 95 (en centroide) → +20
  //       - supraScore 70 (límite radio)  → 0 (neutral)
  let supramunicipalApplied = false;
  if (spatialValid === false && supramunicipalCtx) {
    const supraResult = validateSupramunicipal(
      supramunicipalCtx.x,
      supramunicipalCtx.y,
      supramunicipalCtx.codigoINE,
      supramunicipalCtx.infrastructureType,
    );
    if (supraResult.reason === 'supramunicipal_valid') {
      spatialBonus = Math.round(((supraResult.score - 70) / 25) * 20);
      supramunicipalApplied = true;
    }
  }

  rawScore += spatialBonus;

  // 3. Validación cruzada
  const crossBonus = computeCrossValidationBonus(sourcesConfirming, crossValidationScore, primaryConfidence);
  rawScore += crossBonus;

  // 4. Penalización por discrepancia
  const discrepancyPenalty = computeDiscrepancyPenalty(maxDiscrepancyMeters);
  rawScore -= discrepancyPenalty;

  // 5. Clamp [0, 100]
  const score = Math.max(0, Math.min(100, rawScore));

  // Determinar si pasó validación cruzada
  const crossValidated = sourcesConfirming >= 2 && discrepancyPenalty <= 10;

  // Desglose legible
  const parts: string[] = [`base=${BASE_SCORE}`];
  if (spatialBonus !== 0) {
    const spatialLabel = supramunicipalApplied
      ? `supramunicipal`
      : `spatial=${String(spatialValid)}`;
    parts.push(`${spatialBonus > 0 ? '+' : ''}${spatialBonus} (${spatialLabel})`);
  }
  if (crossBonus !== 0) {
    parts.push(`${crossBonus > 0 ? '+' : ''}${crossBonus} (cross: ${sourcesConfirming} fuentes)`);
  }
  if (discrepancyPenalty > 0) {
    parts.push(`-${discrepancyPenalty} (discr=${maxDiscrepancyMeters}m)`);
  }

  return {
    score,
    spatialValid,
    crossValidated,
    sourcesConfirming,
    discrepancyPenalty,
    spatialBonus,
    detail: `D3=${score}: ${parts.join(', ')}`,
  };
}

// ============================================================================
// UTILIDADES
// ============================================================================

/**
 * Calcula bonus/penalización por validación espacial.
 *
 * - true: +25 (coordenada dentro del municipio)
 * - false: −30 (fuera del municipio, sospechoso)
 * - undefined: 0 (no se pudo verificar)
 */
export function computeSpatialBonus(spatialValid: boolean | undefined): number {
  if (spatialValid === true) return SPATIAL_VALID_BONUS;
  if (spatialValid === false) return -SPATIAL_INVALID_PENALTY;
  return 0;
}

/**
 * Calcula bonus por validación cruzada multi-fuente.
 *
 * - ≥2 fuentes + crossValidationScore: bonus según rangos
 * - ≥2 fuentes sin score: bonus base (10)
 * - 1 fuente + primaryConfidence ≥ 80: bonus proporcional [0, 8]
 *   Fórmula: floor((primaryConfidence - 80) / 20 * 8)
 *   Ejemplos: conf=80→0, conf=85→2, conf=97→6, conf=100→8
 * - 1 fuente sin primaryConfidence, o conf < 80: 0
 * - 0 fuentes: −5 (penalización por ausencia)
 *
 * @param primaryConfidence - Confianza de la fuente primaria (0-100), opcional.
 *   Solo se usa cuando sourcesConfirming === 1.
 */
export function computeCrossValidationBonus(
  sourcesConfirming: number,
  crossValidationScore: number | undefined,
  primaryConfidence?: number,
): number {
  if (sourcesConfirming === 0) return -NO_SOURCES_PENALTY;
  if (sourcesConfirming === 1) {
    // Bonus proporcional solo si la única fuente tiene confianza alta (≥ 80)
    if (primaryConfidence !== undefined && primaryConfidence >= 80) {
      return Math.floor((primaryConfidence - 80) / 20 * 8); // [0, 8]
    }
    return 0;
  }

  // ≥2 fuentes confirman
  if (crossValidationScore === undefined) return CROSS_VALIDATION_BASE_BONUS;

  // Score de cross-validation disponible: buscar rango
  for (const { minScore, bonus } of CROSS_SCORE_BONUS) {
    if (crossValidationScore >= minScore) return bonus;
  }

  return CROSS_VALIDATION_BASE_BONUS;
}

/**
 * Calcula penalización por discrepancia entre fuentes.
 *
 * - undefined: 0 (no hay dato de discrepancia)
 * - ≤25 m: 0 (excelente)
 * - 25-100 m: 10 (menor)
 * - 100-500 m: 20 (moderada)
 * - >500 m: 30 (severa)
 */
export function computeDiscrepancyPenalty(maxDiscrepancyMeters: number | undefined): number {
  if (maxDiscrepancyMeters === undefined) return 0;

  for (const { maxMeters, penalty } of DISCREPANCY_THRESHOLDS) {
    if (maxDiscrepancyMeters <= maxMeters) return penalty;
  }

  // Fallback: penalización máxima
  return 30;
}

/**
 * Crea D3ValidacionInput a partir de datos existentes.
 * Función puente para facilitar la migración scoring v1 → v2.
 *
 * @param spatialValid - ¿Coordenada dentro del municipio?
 * @param crossValidationScore - Score cross-validation (0-100)
 * @param sourcesConfirming - Nº de fuentes que confirman
 * @param maxDiscrepancyMeters - Discrepancia máxima entre fuentes (m)
 * @param primaryConfidence - Confianza fuente primaria (0-100); propagado a D3 solo cuando sourcesConfirming===1
 */
export function buildD3Input(
  spatialValid?: boolean,
  crossValidationScore?: number,
  sourcesConfirming?: number,
  maxDiscrepancyMeters?: number,
  primaryConfidence?: number,
): D3ValidacionInput {
  return {
    spatialValid,
    crossValidationScore,
    sourcesConfirming: sourcesConfirming ?? 0,
    maxDiscrepancyMeters,
    primaryConfidence,
  };
}
